/**
 * product-media.js
 * Gallery controller for snippets/product-media.liquid
 * Task: K-056 (original) + PDP gallery overhaul (2026-05-19)
 *
 * Responsibilities:
 *  1. Hover-zoom (desktop @media hover:hover) — unified side-panel lens to the
 *     right of the HERO. Any element marked `[data-zoom-source]` (hero or any
 *     thumb) acts as a zoom trigger; on hover the shared panel populates with
 *     that element's full-size image and tracks the cursor.
 *  2. Mobile tap-to-zoom — opens K-024 modal with full-res image.
 *  3. Variant-change → filters thumb visibility (hide thumbs that belong to
 *     other variants).
 *  4. Mobile horizontal carousel — dot indicators sync with scroll.
 *
 * Thumbs are NOT click-to-swap — each image stays in place. Removed in the
 * 2026-05-19 overhaul per merchant request.
 *
 * No framework. No npm packages.
 */

(function () {
  'use strict';

  /**
   * Returns true when the device supports hover (i.e. has a mouse/trackpad).
   * Checked once on init so we don't query media on every mouse-move.
   * @returns {boolean}
   */
  function supportsHover() {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  /**
   * Initialises a single `.product-media` component.
   * @param {HTMLElement} root
   */
  function init(root) {
    var primaryWrap = root.querySelector('[data-product-media-primary]');
    var primaryImg = primaryWrap ? primaryWrap.querySelector('.product-media__primary-img') : null;
    var zoomPanel = root.querySelector('[data-zoom-panel]');
    var thumbsWrap = root.querySelector('.product-media__thumbs');
    var thumbEls = thumbsWrap ? Array.from(thumbsWrap.querySelectorAll('.product-media__thumb')) : [];

    var zoomModal = document.getElementById('product-media-zoom');
    var zoomViewerImg = zoomModal ? zoomModal.querySelector('[data-zoom-viewer-img]') : null;

    var hasHover = supportsHover();

    /* ── Unified hover-zoom (desktop only) ──────────────────────────────────
       The zoom panel is a child of the HERO so it positions next to the hero
       regardless of which element triggered the zoom. Any `[data-zoom-source]`
       element (hero or thumb) becomes a trigger:
         - mouseenter: populate panel bg with that element's image
         - mousemove:  update background-position from cursor coords
         - mouseleave: hide panel
       Hero image NEVER transforms — the panel renders the zoom. */

    if (zoomPanel && hasHover) {
      var zoomSources = Array.from(root.querySelectorAll('[data-zoom-source]'));

      /**
       * Returns the image URL to use as the zoom background for `el`.
       * Priority: data-full-src → inner <img> currentSrc/src.
       * @param {HTMLElement} el
       * @returns {string}
       */
      function zoomSrcFor(el) {
        if (el.dataset && el.dataset.fullSrc) return el.dataset.fullSrc;
        var img = el.querySelector('img');
        if (img) return img.currentSrc || img.src || '';
        return '';
      }

      var activeZoomSource = null;

      /**
       * Measures the bottom edge of whatever site header is currently
       * occupying the top of the viewport. Detection is geometry-based, not
       * CSS-property-based, because the sticky behavior may live on a
       * parent wrapper (e.g. `shopify-section-group-header`) rather than
       * the visible header element itself. If the header's bounding rect
       * sits at the top of the viewport with positive height, treat it as
       * occupying that space regardless of `position` value.
       */
      function stickyHeaderBottom() {
        var header = document.querySelector('.site-header, header.header, .header');
        if (!header) return 0;
        var r = header.getBoundingClientRect();
        if (r.top <= 0 && r.bottom > 0) return r.bottom;
        return 0;
      }

      function positionZoomFor(el) {
        /* Place the panel beside the hovered source. Uses fixed positioning
           so it sits in viewport coords. Top floor is the sticky header's
           bottom edge (so the panel never hides behind the nav); bottom is
           viewport - 16. Height capped to fit between them. */
        var rect = el.getBoundingClientRect();
        var vh = window.innerHeight;
        var topMargin = stickyHeaderBottom() + 16;
        var bottomMargin = 16;
        var maxPanelH = vh - topMargin - bottomMargin;
        var panelH = Math.min(rect.height, maxPanelH);
        var top = rect.top;
        if (top + panelH > vh - bottomMargin) {
          top = vh - bottomMargin - panelH;
        }
        if (top < topMargin) top = topMargin;
        zoomPanel.style.top = top + 'px';
        zoomPanel.style.left = rect.right + 16 + 'px';
        zoomPanel.style.height = panelH + 'px';
      }

      /* Scroll re-position — runs while a zoom is active so the panel follows
         the moving thumb as the page scrolls under a stationary cursor. rAF-
         throttled to one update per frame so heavy scrolling doesn't reflow
         the panel position 100+ times per second. */
      var rafPending = false;
      function onScroll() {
        if (!activeZoomSource || rafPending) return;
        rafPending = true;
        requestAnimationFrame(function () {
          rafPending = false;
          if (activeZoomSource) positionZoomFor(activeZoomSource);
        });
      }
      window.addEventListener('scroll', onScroll, { passive: true });

      function showZoom(el) {
        var src = zoomSrcFor(el);
        if (!src) return;
        activeZoomSource = el;
        positionZoomFor(el);
        zoomPanel.style.backgroundImage = 'url("' + src + '")';
        zoomPanel.classList.add('is-visible');
      }

      function moveZoom(el, e) {
        var rect = el.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) return;
        var xPct = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
        var yPct = Math.min(Math.max((e.clientY - rect.top) / rect.height, 0), 1);
        zoomPanel.style.backgroundPosition = (xPct * 100) + '% ' + (yPct * 100) + '%';
      }

      function hideZoom() {
        activeZoomSource = null;
        zoomPanel.classList.remove('is-visible');
      }

      zoomSources.forEach(function (el) {
        el.addEventListener('mouseenter', function () { showZoom(el); });
        el.addEventListener('mousemove', function (e) { moveZoom(el, e); });
        el.addEventListener('mouseleave', hideZoom);
      });
    }

    /* ── Variant change → filter thumb visibility ──────────────────────────── */

    /* Walk up to the parent product section so we can listen for variant:change. */
    var section = root.closest('[id^="product-"]');
    if (section && thumbEls.length > 0) {
      section.addEventListener('variant:change', function (e) {
        var variant = e.detail && e.detail.variant;
        if (!variant) return;

        var newId = String(variant.id);
        root.dataset.currentVariantId = newId;

        var visibleCount = 0;

        thumbEls.forEach(function (el) {
          var owners = (el.dataset.ownerVariants || '').split(',').filter(Boolean);
          var isShared = owners.length === 0;
          var isOwnedByCurrent = owners.indexOf(newId) !== -1;
          var visible = isShared || isOwnedByCurrent;

          if (visible) {
            el.hidden = false;
            el.removeAttribute('aria-hidden');
            el.classList.remove('is-hidden');
            visibleCount++;
          } else {
            el.hidden = true;
            el.setAttribute('aria-hidden', 'true');
            el.classList.add('is-hidden');
          }
        });

        /* Hide the thumbs container entirely when 0 visible — keep it when
           exactly 1 visible (still a useful zoom target). */
        if (thumbsWrap) {
          thumbsWrap.hidden = visibleCount < 1;
        }
      });
    }

    /* ── Mobile horizontal carousel — sync dot indicators with scroll ───────── */
    /* The mobile gallery is a sibling .product-media__mobile under the same
       root. We track scroll position on the track and toggle .is-active on
       the dot whose index matches the centered slide. */
    var mobileTrack = root.querySelector('[data-mobile-track]');
    var mobileDots  = root.querySelectorAll('[data-mobile-dots] .product-media__mobile-dot');

    if (mobileTrack && mobileDots.length > 1) {
      var rafId = 0;
      var setActiveDot = function () {
        rafId = 0;
        var slideW = mobileTrack.clientWidth;
        if (slideW <= 0) return;
        var idx = Math.round(mobileTrack.scrollLeft / slideW);
        if (idx < 0) idx = 0;
        if (idx > mobileDots.length - 1) idx = mobileDots.length - 1;
        mobileDots.forEach(function (dot, i) {
          if (i === idx) {
            dot.classList.add('is-active');
          } else {
            dot.classList.remove('is-active');
          }
        });
      };
      mobileTrack.addEventListener('scroll', function () {
        if (rafId) return;
        rafId = window.requestAnimationFrame(setActiveDot);
      }, { passive: true });
      window.addEventListener('resize', function () {
        if (rafId) return;
        rafId = window.requestAnimationFrame(setActiveDot);
      });
    }

    /* ── Mobile tap-to-zoom (touch / non-hover devices) ────────────────────── */

    function openZoomFromImg(img) {
      if (!zoomModal || !img) return;
      var fullSrc = img.dataset.fullSrc || img.currentSrc || img.src;
      var altText = img.alt || '';
      if (zoomViewerImg && fullSrc) {
        zoomViewerImg.src = fullSrc;
        zoomViewerImg.alt = altText;
      }
      if (typeof zoomModal.showModal === 'function') {
        zoomModal.showModal();
        zoomModal.dispatchEvent(new CustomEvent('dialog:open', { bubbles: true }));
      }
    }

    if (primaryWrap && zoomModal) {
      primaryWrap.addEventListener('click', function () {
        /* Only open modal on mobile / non-hover devices */
        if (hasHover) return;

        var fullSrc = primaryWrap.dataset.fullSrc || (primaryImg ? primaryImg.src : '');
        var altText = primaryImg ? primaryImg.alt : '';

        if (zoomViewerImg && fullSrc) {
          zoomViewerImg.src = fullSrc;
          zoomViewerImg.alt = altText;
        }

        if (typeof zoomModal.showModal === 'function') {
          zoomModal.showModal();
          zoomModal.dispatchEvent(new CustomEvent('dialog:open', { bubbles: true }));
        }
      });
    }

    /* Mobile carousel images — tap to open zoom modal. */
    if (mobileTrack && zoomModal) {
      mobileTrack.addEventListener('click', function (event) {
        var img = event.target.closest && event.target.closest('.product-media__mobile-img');
        if (!img) return;
        openZoomFromImg(img);
      });
    }
  }

  /* ── Boot ─────────────────────────────────────────────────────────────────── */

  document.querySelectorAll('[data-product-media]').forEach(init);
})();
